// Helper utilities for Savidna CRM

export const generateId = (): string => {
  return `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`
}

export const formatCurrency = (amount: number): string => {
  return new Intl.NumberFormat('es-DO', {
    style: 'currency',
    currency: 'DOP',
  }).format(amount)
}

export const formatDate = (date: Date | string): string => {
  const d = typeof date === 'string' ? new Date(date) : date
  return d.toLocaleDateString('es-DO', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
    timeZone: 'America/Santo_Domingo',
  })
}

export const formatShortDate = (date: Date | string): string => {
  const d = typeof date === 'string' ? new Date(date) : date
  return d.toLocaleDateString('es-DO', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    timeZone: 'America/Santo_Domingo',
  })
}

export const formatTime = (time: string): string => {
  const [hours, minutes] = time.split(':')
  const hour = parseInt(hours)
  const ampm = hour >= 12 ? 'PM' : 'AM'
  const hour12 = hour % 12 || 12
  return `${hour12}:${minutes} ${ampm}`
}

export const formatDateTime = (date: string, time: string): string => {
  return `${formatDate(date)} a las ${formatTime(time)}`
}

export const getToday = (): string => {
  const today = new Date()
  return today.toISOString().split('T')[0]
}

export const getTomorrow = (): string => {
  const tomorrow = new Date()
  tomorrow.setDate(tomorrow.getDate() + 1)
  return tomorrow.toISOString().split('T')[0]
}

export const getDaysInMonth = (year: number, month: number): number => {
  return new Date(year, month + 1, 0).getDate()
}

export const getFirstDayOfMonth = (year: number, month: number): number => {
  return new Date(year, month, 1).getDay()
}

export const getMonthName = (month: number): string => {
  const months = [
    'Enero',
    'Febrero',
    'Marzo',
    'Abril',
    'Mayo',
    'Junio',
    'Julio',
    'Agosto',
    'Septiembre',
    'Octubre',
    'Noviembre',
    'Diciembre',
  ]
  return months[month]
}

export const getDayName = (day: number): string => {
  const days = ['Dom', 'Lun', 'Mar', 'Mié', 'Jue', 'Vie', 'Sáb']
  return days[day]
}

export const getStatusColor = (status: string): string => {
  switch (status) {
    case 'programada':
      return 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200'
    case 'completada':
      return 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200'
    case 'cancelada':
      return 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200'
    case 'reprogramada':
      return 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200'
    default:
      return 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-200'
  }
}

export const getStageColor = (stage: string): string => {
  switch (stage) {
    case 'contacto_inicial':
      return 'bg-gray-500'
    case 'evaluacion':
      return 'bg-blue-500'
    case 'procedimiento_programado':
      return 'bg-yellow-500'
    case 'completado':
      return 'bg-green-500'
    case 'facturacion':
      return 'bg-purple-500'
    default:
      return 'bg-gray-500'
  }
}

export const getStageName = (stage: string): string => {
  switch (stage) {
    case 'contacto_inicial':
      return 'Contacto Inicial'
    case 'evaluacion':
      return 'Evaluación'
    case 'procedimiento_programado':
      return 'Procedimiento Programado'
    case 'completado':
      return 'Completado'
    case 'facturacion':
      return 'Facturación'
    default:
      return stage
  }
}

export const getStatusName = (status: string): string => {
  switch (status) {
    case 'programada':
      return 'Programada'
    case 'completada':
      return 'Completada'
    case 'cancelada':
      return 'Cancelada'
    case 'reprogramada':
      return 'Reprogramada'
    default:
      return status
  }
}

export function getRoleName(role: string): string {
  const roleNames: Record<string, string> = {
    administrador: 'Administrador',
    doctor: 'Doctor',
    recepcionista: 'Recepcionista',
    callcenter: 'Call Center',
    medico: 'Médico',
    recepcion: 'Recepción',
    ventas: 'Ventas',
    marketing: 'Marketing',
    soporte: 'Soporte',
  }
  return roleNames[role] || role
}

export const getGenderName = (gender: string): string => {
  switch (gender) {
    case 'masculino':
      return 'Masculino'
    case 'femenino':
      return 'Femenino'
    case 'otro':
      return 'Otro'
    default:
      return gender
  }
}

export const truncateText = (text: string, maxLength: number): string => {
  if (text.length <= maxLength) return text
  return text.substring(0, maxLength) + '...'
}

export const debounce = <T extends (...args: unknown[]) => void>(
  func: T,
  wait: number,
): ((...args: Parameters<T>) => void) => {
  let timeout: NodeJS.Timeout | null = null
  return (...args: Parameters<T>) => {
    if (timeout) clearTimeout(timeout)
    timeout = setTimeout(() => func(...args), wait)
  }
}
